// src/components/ApplyModal.jsx
import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Toast from './Toast';

export default function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    resumeLink: '',
    coverLetter: '',
    experience: '',
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, 'job_applications'), {
        name: form.name,
        email: form.email,
        phone: form.phone,
        linkedin: form.linkedin,
        resumeUrl: form.resumeLink,   // stored as resumeUrl so admin table works
        coverLetter: form.coverLetter,
        experience: form.experience,
        jobId: job.id,
        jobTitle: job.title,
        submittedAt: serverTimestamp(),
        source: 'website',
        status: 'new',
      });

      setToast({ message: "Application submitted! We'll review it and reach out soon.", type: 'success' });
      setTimeout(() => { onClose(); }, 2500);
    } catch (err) {
      console.error(err);
      setToast({ message: 'Submission failed. Please email us at admin@districtsourcingtech.com', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '40px',
        width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
        position: 'relative',
      }}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px', color: 'var(--token-desc)' }}
        >×</button>

        <h2 className="title-5" style={{ marginBottom: '4px' }}>Apply for {job.title}</h2>
        <p className="body-sm" style={{ color: 'var(--token-desc)', marginBottom: '32px' }}>
          {job.type || job.period} · {job.level}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="form-group">
            <label className="form-label" htmlFor="apply-name">Full Name <span className="required">*</span></label>
            <input id="apply-name" name="name" type="text" className="form-input" placeholder="Your full name" value={form.name} onChange={handleChange} required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="apply-email">Email <span className="required">*</span></label>
              <input id="apply-email" name="email" type="email" className="form-input" placeholder="Email address" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="apply-phone">Phone</label>
              <input id="apply-phone" name="phone" type="tel" className="form-input" placeholder="+1 (000) 000-0000" value={form.phone} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="apply-linkedin">LinkedIn Profile</label>
            <input id="apply-linkedin" name="linkedin" type="url" className="form-input" placeholder="https://linkedin.com/in/..." value={form.linkedin} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="apply-experience">Years of Experience <span className="required">*</span></label>
            <div className="form-select-wrap">
              <select id="apply-experience" name="experience" className="form-select" value={form.experience} onChange={handleChange} required>
                <option value="">Select experience level</option>
                <option value="0-1">0–1 years</option>
                <option value="1-3">1–3 years</option>
                <option value="3-5">3–5 years</option>
                <option value="5-10">5–10 years</option>
                <option value="10+">10+ years</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="apply-resume">
              Resume Link{' '}
              <span className="body-sm" style={{ color: 'var(--token-desc)', fontWeight: 400 }}>
                (Google Drive, Dropbox, or any shareable URL)
              </span>
            </label>
            <input
              id="apply-resume"
              name="resumeLink"
              type="url"
              className="form-input"
              placeholder="https://drive.google.com/file/..."
              value={form.resumeLink}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="apply-cover">Cover Letter</label>
            <textarea id="apply-cover" name="coverLetter" className="form-textarea" placeholder="Tell us why you're a great fit..." value={form.coverLetter} onChange={handleChange} />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
          >
            {loading && <span className="spinner" />}
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}